import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getIconComponent } from '../../utils/icons';
import { TestIds } from '../../constants/testIds';
import { type AppConfig } from '../../types';
import { PinButton } from './PinButton';

interface ProductsTableProps {
  readonly apps: AppConfig[];
  readonly pinnedPaths: Set<string>;
  readonly pinDisabled?: boolean;
  readonly onTogglePin: (basePath: string) => void;
  readonly onNavigate: (app: AppConfig) => void;
}

export function ProductsTable({
  apps,
  pinnedPaths,
  pinDisabled,
  onTogglePin,
  onNavigate,
}: ProductsTableProps) {
  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table data-testid={TestIds.ALL_PRODUCTS_TABLE}>
        <TableHead>
          <TableRow>
            <TableCell width={48} />
            <TableCell>Product Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell width={64} align="center">
              Pin
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {apps.map((app) => {
            const IconComponent = getIconComponent(app.icon);
            const isPinned = pinnedPaths.has(app.basePath);
            return (
              <TableRow
                key={app.basePath}
                hover
                role="button"
                aria-label={app.displayName}
                onClick={() => onNavigate(app)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter') onNavigate(app);
                  if (e.key === ' ') {
                    e.preventDefault();
                    onNavigate(app);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-2px',
                  },
                }}
              >
                <TableCell>
                  <IconComponent aria-label={app.displayName} sx={{ fontSize: 24 }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {app.displayName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{app.description ?? '—'}</Typography>
                </TableCell>
                <TableCell align="center">
                  <PinButton
                    displayName={app.displayName}
                    isPinned={isPinned}
                    pinDisabled={pinDisabled}
                    onTogglePin={() => onTogglePin(app.basePath)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
